/**
 * Survey Service
 * Maneja todas las llamadas a la API del backend para el survey
 */

// Configuración del API
const API_BASE_URL = 'https://tyggldxegh.execute-api.us-west-2.amazonaws.com/v1';
const API_KEY = 'Bx8vXrF42K94p7NJcd9oA5qPONArKRER7gC1w79E';

/**
 * Función utilitaria para hacer llamadas a la API
 */
const apiCall = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  console.log('🚀 API Call:', url);
  console.log('📤 Request data:', options.body || 'No body');
  console.log('📤 Request options:', JSON.stringify(options, null, 2));
  
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': API_KEY, 
    },
  };

  const finalOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
    },
  };

  console.log('🔧 Final request options:', JSON.stringify(finalOptions, null, 2));

  try {
    const response = await fetch(url, finalOptions);
    console.log('📥 Response status:', response.status);
    console.log('📥 Response headers:', Object.fromEntries(response.headers.entries()));
    
    if (!response.ok) {
      const errorData = await response.text();
      console.error('❌ API Error Response:', errorData);
      
     
      let parsedError;
      try {
        parsedError = JSON.parse(errorData);
        console.error('📋 Parsed error details:', parsedError);
      } catch {
        parsedError = { message: errorData };
      }
      
      throw new Error(`HTTP error! status: ${response.status}, message: ${JSON.stringify(parsedError)}`);
    }

    const result = await response.json();
    console.log('✅ API Success Response:', result);
    return result;
  } catch (error) {
    console.error('💥 API Call failed:', error);
    throw error;
  }
};

/**
 * Valida un token de reCAPTCHA con el backend
 * @param {string} token - Token de reCAPTCHA
 * @param {string} action 
 * @param {number} minScore - Puntuación mínima (0.0-1.0)
 * @returns {Promise<Object>} Resultado de la validación
 */
export const validateRecaptcha = async (token, action = 'submit_survey', minScore = 0.5) => {
  try {
    console.log('🔒 Validating reCAPTCHA token:', { token: token.substring(0, 20) + '...', action, minScore });
    
    const requestData = {
      token: token,
      expected_action: action,
      min_score: minScore
    };

    const response = await apiCall('/survey/validate-recaptcha', {
      method: 'POST',
      body: JSON.stringify(requestData)
    });

    return {
      success: response.success || false,
      score: response.score || 0,
      action: response.action || action,
      error_codes: response.error_codes || []
    };
  } catch (error) {
    console.error('❌ Error validating reCAPTCHA:', error);
    throw new Error('Error en la verificación de reCAPTCHA');
  }
};

/**
 * Envía las respuestas del survey al backend
 * @param {Object} surveyData - Datos del survey
 * @returns {Promise<Object>} Respuesta del backend
 */
export const submitSurvey = async (surveyData) => {
  try {
    console.log('📝 Preparing survey data for submission:', surveyData);
    
    // Crear un objeto para mapear las respuestas a question_1, question_2, etc.
    const questionAnswers = {};
    let questionIndex = 1;
    let extractedEmail = surveyData.email || '';
    
    // Procesaremos todas las preguntas pero manejaremos question_26 especialmente
    
    // Iterar sobre todas las respuestas y asignarlas secuencialmente
    for (const [questionId, answer] of Object.entries(surveyData)) {
      if (questionId !== 'email') {
        const questionKey = `question_${questionIndex}`;
        
        console.log(`🔄 Processing ${questionId} -> ${questionKey}`);
        
        // Convertir la respuesta a string o JSON según su tipo
        if (answer === null || answer === undefined) {
          questionAnswers[questionKey] = null;
        } else if (typeof answer === 'string') {
          console.log(`🔍 Processing string answer for ${questionKey}:`, answer);
          
          // Intentar extraer email si es un JSON string
          try {
            const parsed = JSON.parse(answer);
            console.log(`🎯 Parsed JSON for ${questionKey}:`, parsed);
            
            if (parsed.email && parsed.email.length > 0) {
              extractedEmail = parsed.email;
              console.log('✅ Email extraído de respuesta:', extractedEmail);
            }
            
            // Para conditional-email, solo guardar la choice, no el objeto completo
            if (parsed.choice !== undefined) {
              questionAnswers[questionKey] = parsed.choice;
              console.log(`📝 Extrayendo solo choice de ${questionKey}:`, parsed.choice);
            } else {
              questionAnswers[questionKey] = answer;
            }
          } catch (e) {
            // No es JSON, usar el valor tal como está
            console.log(`ℹ️ Not JSON for ${questionKey}, using as is:`, answer);
            questionAnswers[questionKey] = answer;
          }
        } else {
          console.log(`🔍 Processing object answer for ${questionKey}:`, answer);
          
          // Es un objeto, manejarlo directamente
          if (answer.email && answer.email.length > 0) {
            extractedEmail = answer.email;
            console.log('✅ Email extraído de objeto:', extractedEmail);
          }
          
          // Para conditional-email, solo guardar la choice
          if (answer.choice !== undefined) {
            questionAnswers[questionKey] = answer.choice;
            console.log(`📝 Extrayendo solo choice de objeto ${questionKey}:`, answer.choice);
          } else {
            questionAnswers[questionKey] = JSON.stringify(answer);
          }
        }
        
        questionIndex++;
      }
    }
    
    // Si no hay email, mantenerlo vacío (es opcional)
    if (!extractedEmail) {
      extractedEmail = '';
      console.log('ℹ️ Email vacío - es opcional');
    }
    
    // Preparar payload según el DTO esperado por el backend
    // Usar el mismo formato que funciona en Postman
    const payload = {
      survey: {
        email: extractedEmail,
        title: 'Zenit AI Survey',
        description: 'Survey sobre automatización y IA',
        ...questionAnswers,
        is_completed: false,
        completion_percentage: 40.0,
        notes: 'Survey completado desde frontend'
      }
    };

    console.log('📤 Final payload to send:', JSON.stringify(payload, null, 2));
    console.log('🔍 Payload keys:', Object.keys(payload));
    console.log('🔍 Survey keys:', Object.keys(payload.survey));
    console.log('🔍 Total questions in payload:', Object.keys(payload.survey).filter(key => key.startsWith('question_')).length);

    // Usar el endpoint original con headers mínimos para evitar CORS
    const response = await apiCall('/survey', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY
      },
      body: JSON.stringify(payload)
    });

    return response;
  } catch (error) {
    console.error('❌ Error submitting survey:', error);
    throw new Error('Error al enviar las respuestas del survey');
  }
};

const surveyService = {
  validateRecaptcha,
  submitSurvey
};

export default surveyService;
